/**
 * First settled /status for this visit only.
 *
 * Belongs here: redirect to the hub when status firstPlanDropTime is a finite number.
 * Does not belong here: the status context value (see ExpertReviewStatusContext).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import ThreeLineLoading from '../../../../components/ui-components/loading/ThreeLineLoading'
import { useExpertReviewNavigation } from '../navigation/expertReviewNavigation'
import { isExpertReviewHubPath } from '../navigation/expertReviewPaths'
import {
  firstPlanDropTimeFromStatus,
  useExpertReviewStatusContext
} from './ExpertReviewStatusContext'

/** A finite firstPlanDropTime replaces the opened route with the hub. Later refetches do not redirect. */
export function ExpertReviewInitialStatusGate({ children }: { children: ReactNode }) {
  const { statusData, isStatusLoading, isStatusError } = useExpertReviewStatusContext()
  const { pathname } = useLocation()
  const { goToHub } = useExpertReviewNavigation()
  const onHub = isExpertReviewHubPath(pathname)
  const hasDecidedRef = useRef(false)
  const [showPage, setShowPage] = useState(false)

  useEffect(() => {
    if (!hasDecidedRef.current && isStatusLoading) return

    if (!hasDecidedRef.current) {
      hasDecidedRef.current = true
      const dropTime = isStatusError ? null : firstPlanDropTimeFromStatus(statusData)
      if (dropTime != null && !isExpertReviewHubPath(pathname)) {
        try {
          // Hub root drops inner expert-review frames, so Back cannot return to this URL.
          goToHub({ replace: true })
        } catch {
          setShowPage(true)
          return
        }
        // replace updates the URL before the next paint. If it did not, show the opened page.
        if (!isExpertReviewHubPath(window.location.pathname)) {
          setShowPage(true)
        }
        return
      }
      setShowPage(true)
      return
    }

    // Redirect landed. Opening the page before this would paint the URL we are leaving.
    if (isExpertReviewHubPath(pathname)) {
      setShowPage(true)
    }
  }, [isStatusLoading, isStatusError, statusData, pathname, goToHub])

  // Hub has its own skeleton and error. Any other URL keeps the loader until the decision above.
  if (onHub || showPage) {
    return children
  }

  return <ThreeLineLoading />
}
